import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';

// Types
import { CalendarMeeting } from 'Shared/types/CalendarMeeting.type';

// Utils
import PageUrlsUtils from 'Shared/utils/PageUrls.utils';

// Hooks
import { useToggle } from 'Shared/utils/hooks';
import { useTranslation } from 'Shared/utils/hooks/use-translation';

import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';

// Actions
import { setRecurringMeeting, setCalendarMeeting } from 'Shared/actions/workspaces';

// Components
import { WpModal, WpModalHeader, WpModalBody, WpModalFooter } from 'Wpds/components/modal';
import { WpButton, WpButtonGroup } from 'Wpds/components/button';
import WorkspaceSelect from 'Shared/components/WorkspaceSelect/WorkspaceSelect';
import WpToastManager from 'Wpds/components/toast/WpToastManager';
import WpIcon from 'Wpds/components/icon/WpIcon';
import { WpContainer, WpContainerBody } from 'Wpds/components/container';

// Styles
import './ConnectCalendarEventToWorkspaceModal.scss';

type Props = {
  isOpen: boolean;
  toggleIsOpen: () => void;
  calendarMeeting: CalendarMeeting;
};

const ConnectCalendarEventToWorkspaceModal = ({ isOpen, toggleIsOpen, calendarMeeting }: Props) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { user } = useCurrentUser();
  const { tr } = useTranslation();

  const [selected, setSelected] = useState(null);
  const [isSubmitting, toggleIsSubmitting] = useToggle(false);

  const workspaces = useSelector(state => state.workspaces) || [];
  const filteredWorkspaces = workspaces.filter(
    ws => !(Boolean(ws.recurringMeetingId) || Boolean(ws.calendarMeetingId))
  );
  const otherUserEmail =
    calendarMeeting.attendees.length === 2 ? calendarMeeting.attendees.find(a => a !== user.email) : null;
  const recommendedOneOnOne = otherUserEmail
    ? filteredWorkspaces.filter(ws => ws.isOneOnOne && Boolean(ws.users.find(user => user.email === otherUserEmail)))
    : [];

  const recommendedGroups = filteredWorkspaces.filter(
    ws => ws.isGroup && calendarMeeting.name.toLowerCase().includes(ws.name.toLowerCase())
  );

  const recommendedWorkspaces = [...recommendedOneOnOne, ...recommendedGroups];

  const handleSelectWorkspace = value => {
    setSelected(value);
  };

  const handleLinkClick = () => {
    if (!selected) return;
    toggleIsSubmitting();

    const handleRedirectToWorkspace = () => {
      history.push(PageUrlsUtils.makeWorkspaceUrl(selected.id));
    };

    if (calendarMeeting.recurringMeetingId) {
      dispatch(
        setRecurringMeeting({
          recurringMeeting: { id: calendarMeeting.recurringMeetingId },
          workspaceId: selected.id,
        })
      ).then(() => {
        toggleIsSubmitting();
        toggleIsOpen();
        WpToastManager.success(tr('perform.connect.calendar_event_to_workspace.toast_success'));
        handleRedirectToWorkspace();
      });
    } else {
      dispatch(setCalendarMeeting({ calendarMeeting, workspaceId: selected.id, page: 'CALENDAR_WIDGET' })).then(() => {
        toggleIsSubmitting();
        toggleIsOpen();
        WpToastManager.success(tr('perform.connect.calendar_event_to_workspace.toast_success'));
        handleRedirectToWorkspace();
      });
    }
  };

  const baseClassName = 'ConnectCalendarEventToWorkspaceModal';

  return (
    <WpModal isOpen={isOpen} onToggle={toggleIsOpen} size="sm">
      <WpModalHeader>{tr('perform.connect.calendar_event_to_workspace.title')}</WpModalHeader>
      <WpModalBody>
        {tr('perform.connect.calendar_event_to_workspace.body')}
        <div className={`${baseClassName}__select`}>
          <WorkspaceSelect
            label={tr('perform.connect.calendar_event_to_workspace.select_workspace_label')}
            id="link-calendar-to-workspace-select"
            workspaces={filteredWorkspaces}
            onChange={handleSelectWorkspace}
            placeholder={tr('perform.connect.calendar_event_to_workspace.select_workspace_placeholder')}
            value={selected?.id}
            disabled={isSubmitting}
            labelHidden
          />
        </div>
        {recommendedWorkspaces.length > 0 && (
          <div className={`${baseClassName}__recommended-list`}>
            <p>{tr('perform.connect.calendar_event_to_workspace.recommended_agendas')}</p>
            {recommendedWorkspaces.map(ws => (
              <WpContainer
                key={ws.id}
                onClick={() => handleSelectWorkspace(ws)}
                className={`${baseClassName}__recommended-list__item ${
                  selected?.id === ws.id ? `${baseClassName}__recommended-list__item--selected` : ''
                }`}
              >
                <WpContainerBody padding="05">
                  <div className={`${baseClassName}__recommended-list__item__body`}>
                    <div className={`${baseClassName}__recommended-list__item__body__icon`}>
                      {selected?.id === ws.id ? (
                        <WpIcon name="check" iconSize="lg" />
                      ) : (
                        <WpIcon name={ws.isOneOnOne ? 'user' : 'users'} size="lg" />
                      )}
                    </div>
                    <div className={`${baseClassName}__recommended-list__item__body__details`}>
                      <div className={`${baseClassName}__recommended-list__item__body__details__title`}>{ws.name}</div>
                      {ws.description && (
                        <div className={`${baseClassName}__recommended-list__item__body__details__description`}>
                          {ws.description}
                        </div>
                      )}
                    </div>
                  </div>
                </WpContainerBody>
              </WpContainer>
            ))}
          </div>
        )}
      </WpModalBody>
      <WpModalFooter>
        <WpButtonGroup>
          <WpButton variant="tertiary" onClick={toggleIsOpen} disabled={isSubmitting}>
            {tr('perform.common.cancel')}
          </WpButton>
          <WpButton onClick={handleLinkClick} loading={isSubmitting} disabled={!selected}>
            {tr('perform.connect.calendar_event_to_workspace.primary_cta')}
          </WpButton>
        </WpButtonGroup>
      </WpModalFooter>
    </WpModal>
  );
};

export default ConnectCalendarEventToWorkspaceModal;
